import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, MapPin, ExternalLink, Anchor, Handshake, Megaphone } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type PartnerRow = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  blurb: string | null;
  description: string | null;
  logo_url: string | null;
  website: string | null;
  location: string | null;
  tier: "advocate" | "partner" | "anchor";
  services: string[];
  counties: string[];
  featured: boolean;
};

const TIER_META: Record<PartnerRow["tier"], { label: string; icon: typeof Handshake; cls: string }> = {
  advocate: { label: "Advocate", icon: Megaphone, cls: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  partner: { label: "Partner", icon: Handshake, cls: "bg-primary/10 text-primary border-primary/30" },
  anchor: { label: "Anchor", icon: Anchor, cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
};

export default function PartnersDirectory() {
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState<"all" | PartnerRow["tier"]>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["partners-directory"],
    queryFn: async (): Promise<PartnerRow[]> => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("id, slug, name, tagline, blurb, description, logo_url, website, location, tier, services, counties, featured, is_published, status")
        .eq("is_published", true)
        .eq("status", "active")
        .order("featured", { ascending: false })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PartnerRow[];
    },
  });

  const filtered = useMemo(() => {
    const rows = data ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (tierFilter !== "all" && r.tier !== tierFilter) return false;
      if (!needle) return true;
      return [
        r.name,
        r.tagline ?? "",
        r.blurb ?? r.description ?? "",
        ...(r.services ?? []),
        ...(r.counties ?? []),
        r.location ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [data, q, tierFilter]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <section className="px-4 sm:px-6 pt-10 pb-6 max-w-6xl mx-auto">
          <Link to="/partners" className="text-sm text-muted-foreground hover:text-foreground">
            ← Partner Program
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">Partner Directory</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Vetted Advocates, Partners, and Anchors working alongside The Emerging Market across Alabama.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, service, or county…"
                className="pl-9"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {(["all", "advocate", "partner", "anchor"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTierFilter(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border capitalize transition ${
                    tierFilter === t
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t === "all" ? "All tiers" : t}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 sm:px-6 pb-16 max-w-6xl mx-auto">
          {isLoading ? (
            <div className="text-muted-foreground text-sm">Loading partners…</div>
          ) : filtered.length === 0 ? (
            <Card className="p-10 text-center">
              <p className="text-muted-foreground">
                {(data ?? []).length === 0
                  ? "No published partners yet. Approved partners will appear here once their profile is set to published."
                  : "No partners match your filters."}
              </p>
              <Button asChild className="mt-4">
                <Link to="/partners/apply">Apply to the program</Link>
              </Button>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((p) => {
                const Tier = TIER_META[p.tier];
                const Icon = Tier.icon;
                return (
                  <Card key={p.id} className="p-5 flex flex-col">
                    <div className="flex items-start gap-3 mb-3">
                      {p.logo_url ? (
                        <img src={p.logo_url} alt={p.name} className="h-12 w-12 rounded-md object-cover bg-secondary" />
                      ) : (
                        <div className="h-12 w-12 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="h-5 w-5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-foreground truncate">{p.name}</h3>
                          {p.featured && (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[10px]">
                              Featured
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className={Tier.cls}>{Tier.label}</Badge>
                          {p.location && (
                            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> {p.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {(p.blurb || p.tagline || p.description) && (
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                        {p.blurb || p.tagline || p.description}
                      </p>
                    )}

                    {p.services?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {p.services.slice(0, 4).map((s) => (
                          <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                            {s}
                          </span>
                        ))}
                        {p.services.length > 4 && (
                          <span className="text-[11px] px-2 py-0.5 text-muted-foreground">
                            +{p.services.length - 4} more
                          </span>
                        )}
                      </div>
                    )}

                    {p.counties?.length > 0 && (
                      <div className="text-xs text-muted-foreground mb-3">
                        Serves: {p.counties.slice(0, 4).join(", ")}{p.counties.length > 4 ? "…" : ""}
                      </div>
                    )}

                    <div className="mt-auto flex gap-2">
                      <Button asChild variant="outline" size="sm" className="flex-1">
                        <Link to={`/affiliates/${p.slug}`}>View profile</Link>
                      </Button>
                      {p.website && (
                        <Button asChild variant="ghost" size="sm">
                          <a href={p.website} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}